// Pure data shapes + derivation logic for the Insights dashboard.
// Everything here is side-effect free so it can be unit tested.

// ---- Row shapes returned by the get_sales_insights RPC ----
export interface InsightKpis {
  revenue: number;
  paid_orders: number;
  customers: number;
  avg_order_value: number;
  discounts_total: number;
  discounted_orders: number;
  new_customers: number;
  returning_customers: number;
}

export interface TrendPoint {
  day: string; // yyyy-MM-dd
  revenue: number;
  orders: number;
  new_customers: number;
  returning_customers: number;
}

export interface ChannelRow {
  channel: string;
  orders: number;
  revenue: number;
  avg_order_value: number;
}

export interface DiscountStats {
  total_discount: number;
  discounted_orders: number;
  paid_orders: number;
  discount_to_repeat: number;
  avg_basket_discounted: number | null;
  avg_basket_full: number | null;
}

export interface DowRow {
  iso_dow: number; // 1 = Monday … 7 = Sunday
  orders: number;
  revenue: number;
  avg_order_value: number;
}

export interface PairingRow {
  item_a: string;
  item_b: string;
  times_paired: number;
}

export interface LadderRow {
  frequency_segment: string;
  customers: number;
  avg_ltv: number;
  revenue: number;
}

export interface RecencyRow {
  recency_segment: string;
  customers: number;
  lifetime_value: number;
}

export interface LoyaltyRow {
  is_loyalty: boolean;
  customers: number;
  avg_orders: number;
  avg_ltv: number;
}

export interface SecondOrderMonitorRow {
  cohort_month: string; // yyyy-MM-dd (first of month)
  new_customers: number;
  second_within_30d: number;
  pct_second_30d: number | null;
}

export interface MonitorSection {
  /** Last fully-matured monthly cohorts, newest first. */
  second_order: SecondOrderMonitorRow[];
  /** Loyal + superfan customers currently cooling / at risk / lapsing. */
  vip_quiet: { customers: number; lifetime_value: number } | null;
  /** Total loyal + superfan customers (denominator for the quiet share). */
  vip_total: number | null;
}

export interface SalesInsights {
  kpis: InsightKpis | null;
  trend: TrendPoint[];
  channels: ChannelRow[];
  discounts: DiscountStats | null;
  dow: DowRow[];
  pairings: PairingRow[];
  ladder: LadderRow[];
  recency: RecencyRow[];
  loyalty: LoyaltyRow[];
  monitor: MonitorSection | null;
}

// ---- Formatting ----
export const money = (v: unknown): string =>
  `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const money2 = (v: unknown): string =>
  `$${Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const pctOf = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—";

// ---- Segment labels & ordering ----
export const FREQUENCY_ORDER = ["one_time", "occasional", "regular", "loyal", "superfan"] as const;

export const FREQUENCY_LABELS: Record<string, string> = {
  one_time: "1 order",
  occasional: "2–3 orders",
  regular: "4–6 orders",
  loyal: "7–12 orders",
  superfan: "13+ (superfans)",
};

export const RECENCY_ORDER = ["active", "cooling", "at_risk", "lapsing", "lost"] as const;

export const RECENCY_LABELS: Record<string, string> = {
  active: "Active (0–30d)",
  cooling: "Cooling (30–60d)",
  at_risk: "At risk (60–90d)",
  lapsing: "Lapsing (90–180d)",
  lost: "Lost (180d+)",
};

export const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const sortByOrder = <T,>(rows: T[], key: (r: T) => string, order: readonly string[]): T[] =>
  [...rows].sort((a, b) => order.indexOf(key(a)) - order.indexOf(key(b)));

// ---- Trend bucketing: keep charts readable at any range length ----
export type Granularity = "day" | "week" | "month";

export const pickGranularity = (rangeDays: number): Granularity =>
  rangeDays <= 31 ? "day" : rangeDays <= 120 ? "week" : "month";

export interface TrendBucket {
  label: string;
  revenue: number;
  orders: number;
  new_customers: number;
  returning_customers: number;
}

const bucketKey = (day: string, granularity: Granularity): string => {
  // day is yyyy-MM-dd; derive a stable bucket key without timezone surprises.
  const d = new Date(day + "T00:00:00");
  if (granularity === "day") return day;
  if (granularity === "month") return day.slice(0, 7); // yyyy-MM
  // week: snap to Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(
    monday.getDate()
  ).padStart(2, "0")}`;
};

const bucketLabel = (key: string, granularity: Granularity): string => {
  if (granularity === "month") {
    const d = new Date(key + "-01T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  const d = new Date(key + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export function bucketTrend(trend: TrendPoint[], rangeDays: number): TrendBucket[] {
  const granularity = pickGranularity(rangeDays);
  const map = new Map<string, TrendBucket>();
  for (const p of trend) {
    const key = bucketKey(p.day, granularity);
    const cur = map.get(key) ?? {
      label: bucketLabel(key, granularity),
      revenue: 0,
      orders: 0,
      new_customers: 0,
      returning_customers: 0,
    };
    cur.revenue += Number(p.revenue);
    cur.orders += Number(p.orders);
    cur.new_customers += Number(p.new_customers);
    cur.returning_customers += Number(p.returning_customers);
    map.set(key, cur);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([, v]) => ({ ...v, revenue: Math.round(v.revenue) }));
}

// ---- Acquisition verdict: is the top of the funnel growing or shrinking? ----
export interface AcquisitionVerdict {
  direction: "up" | "down" | "flat";
  changePct: number; // new customers, second half vs first half of the range
  firstHalfNew: number;
  secondHalfNew: number;
}

export function acquisitionVerdict(trend: TrendPoint[]): AcquisitionVerdict | null {
  if (trend.length < 4) return null;
  const mid = Math.floor(trend.length / 2);
  const sum = (rows: TrendPoint[]) => rows.reduce((s, r) => s + Number(r.new_customers), 0);
  const firstHalfNew = sum(trend.slice(0, mid));
  const secondHalfNew = sum(trend.slice(mid));
  if (firstHalfNew === 0) return { direction: "flat", changePct: 0, firstHalfNew, secondHalfNew };
  const changePct = Math.round(((secondHalfNew - firstHalfNew) / firstHalfNew) * 100);
  return {
    direction: changePct > 5 ? "up" : changePct < -5 ? "down" : "flat",
    changePct,
    firstHalfNew,
    secondHalfNew,
  };
}

// ---- Weakest weekdays (for slow-day promos) ----
export function weakestDays(dow: DowRow[], count = 2): DowRow[] {
  return [...dow].sort((a, b) => Number(a.revenue) - Number(b.revenue)).slice(0, count);
}

const num = (v: unknown) => Number(v ?? 0);

// ---- Win-back segment rule (mirrors v_customer_lifecycle) ----
// The single source of truth for which recency segments count as win-back.
export const WINBACK_SEGMENTS = ["cooling", "at_risk", "lapsing"];

/** Customers + proven lifetime value sitting in the win-back segments. */
export function winbackTotals(recency: RecencyRow[]): { customers: number; value: number } {
  const segs = recency.filter((r) => WINBACK_SEGMENTS.includes(r.recency_segment));
  return {
    customers: segs.reduce((s, r) => s + num(r.customers), 0),
    value: segs.reduce((s, r) => s + num(r.lifetime_value), 0),
  };
}

// ---- Owner scorecard: the four numbers to check every week ----
export type ScorecardStatus = "good" | "watch" | "bad" | "neutral";

export interface ScorecardTile {
  key: "second_order" | "discount_leak" | "winback" | "vip_quiet";
  title: string;
  value: string;
  status: ScorecardStatus;
  /** What the number is and where it should go. */
  detail: string;
  /** One-line why-it-matters for the owner. */
  why: string;
  /** Where to act on it. */
  actionTo: string;
  actionLabel: string;
}

const cohortLabel = (isoFirstOfMonth: string): string =>
  new Date(isoFirstOfMonth + (isoFirstOfMonth.length === 10 ? "T00:00:00" : "")).toLocaleDateString(
    undefined,
    { month: "short", year: "numeric" }
  );

/**
 * Derives the weekly owner scorecard from the insights payload.
 * Thresholds:
 *  - 2nd-order rate: healthy automated programs run 25-40%; <15% is bleeding.
 *  - Discount leak: share of discount dollars going to repeat customers;
 *    aim ≤33% (discounts should buy acquisition + win-back, not regulars).
 *  - Win-back pipeline: no absolute target — drain it monthly (neutral).
 *  - Quiet VIPs: share of loyal+superfan customers gone quiet; <10% good.
 */
export function buildScorecard(data: SalesInsights): ScorecardTile[] {
  const tiles: ScorecardTile[] = [];

  // 1. 2nd-order conversion (latest matured cohort)
  const cohorts = data.monitor?.second_order ?? [];
  const latest = cohorts[0];
  const prev = cohorts[1];
  if (latest) {
    const pct = num(latest.pct_second_30d);
    tiles.push({
      key: "second_order",
      title: "2nd-order rate",
      value: `${pct}%`,
      status: pct >= 25 ? "good" : pct >= 15 ? "watch" : "bad",
      detail: `${cohortLabel(latest.cohort_month)} cohort: ${latest.second_within_30d} of ${num(latest.new_customers).toLocaleString()} new customers ordered again within 30 days${prev ? ` (${cohortLabel(prev.cohort_month)}: ${num(prev.pct_second_30d)}%)` : ""}. Target 25–40%.`,
      why: "The cheapest growth you have — a 2nd order more than doubles a customer's lifetime value.",
      actionTo: "/revenue-plays?tab=secondorder",
      actionLabel: "Open 2nd-order funnel",
    });
  } else {
    tiles.push({
      key: "second_order",
      title: "2nd-order rate",
      value: "—",
      status: "neutral",
      detail: "No matured cohort yet — a cohort needs its month plus 30 days to finish counting.",
      why: "The cheapest growth you have — a 2nd order more than doubles a customer's lifetime value.",
      actionTo: "/revenue-plays?tab=secondorder",
      actionLabel: "Open 2nd-order funnel",
    });
  }

  // 2. Discount dollars going to repeat customers (range-scoped)
  const d = data.discounts;
  const totalDiscount = num(d?.total_discount);
  const toRepeat = num(d?.discount_to_repeat);
  const leakPct = totalDiscount > 0 ? Math.round((toRepeat / totalDiscount) * 100) : 0;
  tiles.push({
    key: "discount_leak",
    title: "Discounts to regulars",
    value: money(toRepeat),
    status: totalDiscount === 0 ? "neutral" : leakPct <= 33 ? "good" : leakPct <= 66 ? "watch" : "bad",
    detail:
      totalDiscount === 0
        ? "No discounts given in this period."
        : `${leakPct}% of ${money(totalDiscount)} in discounts went to repeat customers this period. Aim ≤33%.`,
    why: "Paying people who were coming back anyway — swap blanket discounts for bonuses aimed at first-timers and win-backs.",
    actionTo: "/offer-designer",
    actionLabel: "Re-target offers",
  });

  // 3. Win-back pipeline (lifetime snapshot)
  const { customers: winbackCustomers, value: winbackValue } = winbackTotals(data.recency);
  tiles.push({
    key: "winback",
    title: "Win-back pipeline",
    value: winbackCustomers.toLocaleString(),
    status: "neutral",
    detail: `${money(winbackValue)} of proven lifetime spend sitting in cooling / at-risk / lapsing. Drain it monthly — these counts should fall week over week.`,
    why: "They already chose you once; a personalized text is your cheapest campaign.",
    actionTo: "/revenue-plays?tab=winback",
    actionLabel: "Get the phone list",
  });

  // 4. VIPs going quiet (lifetime snapshot)
  const vipQuiet = data.monitor?.vip_quiet;
  const vipTotal = num(data.monitor?.vip_total);
  if (vipQuiet && vipTotal > 0) {
    const share = Math.round((num(vipQuiet.customers) / vipTotal) * 100);
    tiles.push({
      key: "vip_quiet",
      title: "VIPs going quiet",
      value: num(vipQuiet.customers).toLocaleString(),
      status: share < 10 ? "good" : share < 25 ? "watch" : "bad",
      detail: `${share}% of your ${vipTotal.toLocaleString()} best customers (7+ orders, ${money(vipQuiet.lifetime_value)} lifetime spend) haven't ordered in 30–180 days.`,
      why: "Your top 3% of customers drive ~half of revenue — a quiet VIP deserves a personal text, not a blast.",
      actionTo: "/revenue-plays?tab=winback&vip=1",
      actionLabel: "Text these first",
    });
  } else {
    tiles.push({
      key: "vip_quiet",
      title: "VIPs going quiet",
      value: "—",
      status: "neutral",
      detail: "VIP data unavailable for this store yet.",
      why: "Your top 3% of customers drive ~half of revenue — a quiet VIP deserves a personal text, not a blast.",
      actionTo: "/revenue-plays?tab=winback&vip=1",
      actionLabel: "Open Revenue Plays",
    });
  }

  return tiles;
}

// ---- Action plan generator ----
export interface Play {
  rank: number;
  title: string;
  why: string;
  steps: string[];
  success: string[];
  impact: string;
}

/**
 * Builds the ranked "what to do first" plan from live data. Order follows
 * impact ÷ effort: retention, win-back, discount targeting, channel shift,
 * slow days. Plays with no supporting data are dropped.
 */
export function buildPriorities(data: SalesInsights): Play[] {
  const plays: Play[] = [];

  const oneTime = data.ladder.find((l) => l.frequency_segment === "one_time");
  const occasional = data.ladder.find((l) => l.frequency_segment === "occasional");
  if (oneTime && num(oneTime.customers) > 0) {
    const ltvJump =
      occasional && num(oneTime.avg_ltv) > 0
        ? `${(num(occasional.avg_ltv) / num(oneTime.avg_ltv)).toFixed(1)}×`
        : "≈2×";
    plays.push({
      rank: plays.length + 1,
      title: 'Launch the "2nd-order" automatic SMS',
      why: `${num(oneTime.customers).toLocaleString()} customers ordered once and never came back. Getting just a 2nd order lifts their value ${ltvJump} (${money(oneTime.avg_ltv)} → ${money(occasional?.avg_ltv)}).`,
      steps: [
        "Open Offer Designer and create a rule: customer's total orders = 1, 7–10 days since their order.",
        'Attach an offer worth coming back for (e.g. "Free garlic sticks with any pizza this week").',
        "Pick the SMS template in Settings → SMS and keep it under 160 characters with the customer's name.",
        "Turn the rule on and leave it running — it works every new customer automatically.",
      ],
      success: [
        "Track the “% 2nd ≤ 30d” column in Revenue Plays → 2nd-Order Funnel every Monday.",
        "Target: lift 2nd-order rate to 25%+ within 60 days (healthy programs hit 25–40%).",
        "If under 15% after 3 weeks, swap the offer — the message isn't compelling enough.",
      ],
      impact: "Highest ROI — uses tools you already own",
    });
  }

  const { customers: winbackCustomers, value: winbackValue } = winbackTotals(data.recency);
  if (winbackCustomers > 0) {
    plays.push({
      rank: plays.length + 1,
      title: "Run a win-back SMS blast",
      why: `${winbackCustomers.toLocaleString()} past customers are cooling, at risk, or lapsing — ${money(winbackValue)} of proven lifetime spend going quiet.`,
      steps: [
        "Open Revenue Plays → Win-Back List and export the CSV (it includes each customer's favorite item).",
        "Send a personalized SMS: “We miss you! Your favorite [item] is calling — here's $5 off this week.”",
        "Start with the “lapsing” segment (highest urgency), then cooling a week later.",
        "Send Thursday 4–6 PM — right when weekend pizza plans are forming.",
      ],
      success: [
        `Target: 10% reactivation ≈ ${Math.round(winbackCustomers * 0.1).toLocaleString()} customers back in the door.`,
        "Watch the Cooling/At-risk and Lapsing counters on Revenue Plays drop week over week.",
        "Repeat monthly — win-back is a habit, not a one-off.",
      ],
      impact: `${money(winbackValue)} at stake`,
    });
  }

  if (data.discounts && num(data.discounts.discount_to_repeat) > 0) {
    const d = data.discounts;
    const recoverable = Math.round(num(d.discount_to_repeat) / 3);
    plays.push({
      rank: plays.length + 1,
      title: "Re-target your discounts",
      why: `${money(d.discount_to_repeat)} of ${money(d.total_discount)} in discounts went to repeat customers who were likely coming back anyway.`,
      steps: [
        'Switch blanket discounts to threshold offers in Offer Designer (e.g. "$5 off $35+") — they protect margin and grow baskets.',
        "Concentrate discount offers on new-customer and win-back rules only.",
        "Exclude your superfans from discount rules — reward them with surprise freebies instead.",
      ],
      success: [
        `Target: reclaim a third of repeat-customer discounts ≈ ${money(recoverable)} straight to profit.`,
        "Check the “Discounts given” KPI on this page monthly — it should fall while revenue holds.",
      ],
      impact: `≈ ${money(recoverable)} margin recovered`,
    });
  }

  const sorted = [...data.channels].sort((a, b) => num(b.avg_order_value) - num(a.avg_order_value));
  const best = sorted[0];
  const biggest = [...data.channels].sort((a, b) => num(b.orders) - num(a.orders))[0];
  if (
    best &&
    biggest &&
    best.channel !== biggest.channel &&
    num(best.avg_order_value) > num(biggest.avg_order_value) * 1.5
  ) {
    const ratio = (num(best.avg_order_value) / num(biggest.avg_order_value)).toFixed(1);
    plays.push({
      rank: plays.length + 1,
      title: `Nudge ${biggest.channel} customers toward ${best.channel}`,
      why: `${best.channel} orders average ${money2(best.avg_order_value)} vs ${money2(biggest.avg_order_value)} for ${biggest.channel} — ${ratio}× bigger baskets, and ${biggest.channel} is your biggest channel (${num(biggest.orders).toLocaleString()} orders).`,
      steps: [
        `Add a banner/offer: "Free delivery on orders over $40" to make switching a no-brainer.`,
        "Mention it in every order-confirmation SMS — that's a free ad to your best audience.",
      ],
      success: [
        "Watch the channel mix table on this page: delivery share of orders should climb monthly.",
        "Guardrail: overall avg order value should rise — if it falls, raise the free-delivery threshold.",
      ],
      impact: `${ratio}× basket size on switched orders`,
    });
  }

  const weak = weakestDays(data.dow, 2);
  if (weak.length === 2 && data.dow.length >= 5) {
    const names = weak.map((w) => DOW_LABELS[w.iso_dow - 1]).join(" & ");
    plays.push({
      rank: plays.length + 1,
      title: `Fill the slow days: ${names}`,
      why: `${names} bring in the least revenue (${weak.map((w) => money(w.revenue)).join(" and ")} this period). Empty ovens are pure fixed cost.`,
      steps: [
        `Create a ${names}-only offer in Offer Designer (e.g. "Two-topping Tuesday — large for the price of a medium").`,
        "Promote it in the win-back and 2nd-order SMS sends so the same messages do double duty.",
      ],
      success: [
        `Compare ${names} revenue on this page month over month — target +20% within 8 weeks.`,
        "Keep the offer day-locked so it shifts demand without discounting the weekend rush.",
      ],
      impact: "Smooths revenue across the week",
    });
  }

  return plays;
}
